/*
  slowride: collection filter engine (custom, no Search & Discovery).
  - Liquid paints page 1 (sections/sr-collection.liquid + snippets/sr-card.liquid).
  - This loads the whole collection from ?view=index (templates/collection.index.liquid), caches it in
    sessionStorage, and filters / sorts / pages in the browser.
  - State lives in the URL (?size=…&brand=…&price=20-60&type=…&color=…&sort=…).
  - Fully sold-out products never show. A picked size shows only products with that exact size in stock.
  - "My size": a picked size is remembered (localStorage srMySize) and applied on every collection page.
  Docs: Shopify Store/collection-engine.md → Theme engine (workspace repo).
*/
(() => {
  if (customElements.get('sr-collection')) return;

  /* ---------- color families (store-wide; later a metaobject) ---------- */
  const FAMILIES = [
    ['black', 'Black', '#111111'],
    ['white', 'White & Cream', '#f4f1ea'],
    ['grey', 'Grey', '#9a9a9a'],
    ['navy', 'Navy', '#1c2a44'],
    ['blue', 'Blue', '#3b6fb6'],
    ['green', 'Green', '#4f6b3a'],
    ['brown', 'Brown & Tan', '#9a7650'],
    ['red', 'Red', '#a3262a'],
    ['orange', 'Orange', '#e07a2e'],
    ['yellow', 'Yellow', '#e9c43b'],
    ['pink', 'Pink & Purple', '#c46aa5'],
    ['multi', 'Camo & Multi', 'conic-gradient(#4f6b3a, #9a7650, #111, #4f6b3a)'],
  ];
  const WORDS = {
    black: 'black', onyx: 'black', jet: 'black', ebony: 'black',
    white: 'white', natural: 'white', bone: 'white', cream: 'white', ivory: 'white', ecru: 'white', eggshell: 'white', vanilla: 'white', oatmeal: 'white', chalk: 'white', cloud: 'white',
    grey: 'grey', gray: 'grey', heather: 'grey', charcoal: 'grey', carbon: 'grey', ash: 'grey', silver: 'grey', graphite: 'grey', slate: 'grey', smoke: 'grey', steel: 'grey', gunmetal: 'grey', pewter: 'grey', cement: 'grey', stone: 'grey',
    navy: 'navy', midnight: 'navy',
    blue: 'blue', denim: 'blue', indigo: 'blue', royal: 'blue', sky: 'blue', teal: 'blue', cyan: 'blue', aqua: 'blue', cobalt: 'blue', turquoise: 'blue', ocean: 'blue', chambray: 'blue',
    green: 'green', olive: 'green', army: 'green', moss: 'green', sage: 'green', forest: 'green', military: 'green', hunter: 'green', kelly: 'green', lime: 'green', mint: 'green', loden: 'green', pine: 'green', fatigue: 'green',
    brown: 'brown', tan: 'brown', khaki: 'brown', desert: 'brown', coyote: 'brown', sand: 'brown', chocolate: 'brown', coffee: 'brown', darkwood: 'brown', clay: 'brown', mocha: 'brown', wheat: 'brown', camel: 'brown', cognac: 'brown', saddle: 'brown', tobacco: 'brown', espresso: 'brown', beige: 'brown', taupe: 'brown', walnut: 'brown', bark: 'brown', dune: 'brown', brick: 'red',
    red: 'red', burgundy: 'red', maroon: 'red', wine: 'red', crimson: 'red', cardinal: 'red', oxblood: 'red', cherry: 'red', scarlet: 'red',
    orange: 'orange', rust: 'orange', copper: 'orange', pumpkin: 'orange', burnt: 'orange', amber: 'orange', blaze: 'orange', coral: 'orange',
    yellow: 'yellow', gold: 'yellow', mustard: 'yellow', butter: 'yellow', lemon: 'yellow', honey: 'yellow',
    pink: 'pink', purple: 'pink', lavender: 'pink', rose: 'pink', magenta: 'pink', violet: 'pink', plum: 'pink', lilac: 'pink', fuchsia: 'pink', mauve: 'pink',
    camo: 'multi', realtree: 'multi', mossy: 'multi', multi: 'multi', tie: 'multi', dye: 'multi', plaid: 'multi', print: 'multi', floral: 'multi', paisley: 'multi', stripe: 'multi', check: 'multi', buffalo: 'multi',
  };
  const familyCache = new Map();
  function familyOf(color) {
    if (!color) return null;
    if (familyCache.has(color)) return familyCache.get(color);
    const words = color.toLowerCase().replace(/[^a-z ]/g, ' ').split(/\s+/).filter(Boolean);
    let fam = null;
    if (words.some((w) => WORDS[w] === 'multi')) fam = 'multi';
    for (let i = words.length - 1; i >= 0 && !fam; i--) fam = WORDS[words[i]] || null; // base color is usually last
    familyCache.set(color, fam);
    return fam;
  }

  /* ---------- sizes ---------- */
  const SIZE_ORDER = ['NB', '0-3M', '3-6M', '6-12M', '12M', '12-18M', '18M', '18-24M', '24M', '2T', '3T', '4T', '5T', '6T', 'YXS', 'YS', 'YM', 'YL', 'YXL', 'XXS', 'XS', 'S', 'S/M', 'M', 'M/L', 'L', 'L/XL', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', 'OS', 'O/S', 'ONE SIZE'];
  function sizeKey(v) {
    let u = String(v).toUpperCase().trim();
    const tall = / TALL$/.test(u) ? 0.5 : 0;
    u = u.replace(/ TALL$/, '');
    const i = SIZE_ORDER.indexOf(u);
    if (i >= 0 && tall) return [0, i + tall, u];
    if (i >= 0) return [0, i, u];
    const n = parseFloat(u);
    if (!Number.isNaN(n)) return [1, n, u];
    return [2, 0, u];
  }
  function sizeSort(a, b) {
    const x = sizeKey(a), y = sizeKey(b);
    return x[0] - y[0] || x[1] - y[1] || (x[2] < y[2] ? -1 : x[2] > y[2] ? 1 : 0);
  }

  /* ---------- size dimensions: size, waist, length, width, lens (display/matching only, product data untouched) ---------- */
  const DIMS = ['size', 'waist', 'length', 'width', 'lens'];
  const LETTER = {
    'XX-SMALL': 'XXS', 'XXSMALL': 'XXS', 'X-SMALL': 'XS', 'XSMALL': 'XS', 'EXTRA SMALL': 'XS', SMALL: 'S', SM: 'S', MEDIUM: 'M', MED: 'M', MD: 'M',
    LARGE: 'L', LG: 'L', 'X-LARGE': 'XL', XLARGE: 'XL', 'EXTRA LARGE': 'XL', 'XX-LARGE': 'XXL', XXLARGE: 'XXL', '2X': 'XXL', '2XL': 'XXL',
    'XXX-LARGE': 'XXXL', '3X': 'XXXL', '3XL': 'XXXL', '4X': '4XL', '5X': '5XL', 'ONE SIZE': 'OS', 'O/S': 'OS', OSFA: 'OS',
  };
  // "Large" → L, "2X" → XXL, Carhartt "L Regular" → L, "L Tall" → "L Tall" (its own chip, after L)
  function normSize(v) {
    let u = String(v).toUpperCase().replace(/\s+/g, ' ').trim().replace(/ REGULAR$/, '');
    const tall = / TALL$/.test(u);
    if (tall) u = u.replace(/ TALL$/, '');
    u = LETTER[u] || u;
    return tall ? `${u} Tall` : u;
  }
  // catch-all product types ("Apparel", "Accessory"): read the real type from the title, for the Type filter only
  const TYPE_FROM_TITLE = [
    [/trucker|snapback|\bcap\b|\bhat\b/i, 'Hat'], [/beanie/i, 'Beanie'], [/boardshort|\btrunk/i, 'Boardshorts'], [/\btank/i, 'Tank Top'],
    [/s\/s woven|oxford|button/i, 'S/S Woven'], [/hood|pullover/i, 'Hoodie'], [/\bsock/i, 'Socks'], [/denim|jean|\bpant/i, 'Pants'], [/\bshort/i, 'Shorts'],
    [/\btee\b|t-shirt/i, 'Tee'], [/wallet|passcase/i, 'Wallet'], [/\bbelt/i, 'Belt'], [/sticker/i, 'Sticker'], [/key ?chain|key keeper/i, 'Keychain'],
    [/air freshener/i, 'Air Freshener'], [/glove/i, 'Gloves'], [/goggle/i, 'Goggles'], [/bag|backpack|duffel|tool roll/i, 'Bag'], [/cooler/i, 'Cooler'],
    [/flag/i, 'Flag'], [/license plate/i, 'License Plate Frame'], [/wax|cleaner|conditioner|odor/i, 'Boot Care'], [/hold-master|pomade/i, 'Grooming'],
  ];
  function displayType(p) {
    if (!/^(apparel|accessory|accessories)$/i.test(p.ty || '')) return p.ty;
    const hit = TYPE_FROM_TITLE.find(([re]) => re.test(p.t));
    return hit ? hit[1] : p.ty;
  }
  const WIDTH_ORDER = ['Narrow', 'Regular', 'Wide', 'Extra Wide'];
  function normWidth(v) {
    const u = String(v).toUpperCase().trim();
    if (/EXTRA|XW|EW|EEE|3E|4E/.test(u)) return 'Extra Wide';
    if (/WIDE|\bEE\b|2E|^W$|\(W\)|^C$|\(C\)/.test(u)) return 'Wide';
    if (/NARROW|^N$/.test(u)) return 'Narrow';
    return 'Regular'; // D, M, Medium, Regular, and B (women's regular)
  }
  const BOTTOMS_RE = /pant|jean|short|trouser|chino|overall|denim|cargo/i;
  const FOOTWEAR_RE = /boot|shoe|sneaker|footwear|sandal|wellington|moc/i;
  // one variant's raw options → {size, waist, length, width, lens}
  function dimsOf(p, opts) {
    const d = {};
    if (p.wi >= 0) d.waist = String(opts[p.wi]).replace(/[^\d.]/g, '') || opts[p.wi];
    if (p.li >= 0) d.length = String(opts[p.li]).replace(/[^\d.]/g, '') || opts[p.li];
    if (p.di >= 0) d.width = normWidth(opts[p.di]);
    if (p.ni >= 0) d.lens = String(opts[p.ni]).trim();
    if (p.si >= 0) {
      const raw = String(opts[p.si]).trim();
      let m;
      if ((m = raw.match(/^W?\s*(\d{2}(?:\.5)?)\s*[xX×\/]\s*L?\s*(\d{2}(?:\.5)?)$/)) || (m = raw.match(/^W\s*(\d{2})\s*L\s*(\d{2})$/i))) {
        d.waist = d.waist || m[1]; d.length = d.length || m[2];
      } else if (/^\d{2}$/.test(raw) && +raw >= 26 && +raw <= 50 && BOTTOMS_RE.test(`${p.ty} ${p.t}`)) {
        d.waist = d.waist || raw; // Brixton, Iron & Resin, Seager: "Size: 32" is the waist
      } else if (FOOTWEAR_RE.test(`${p.ty} ${p.t}`) && (m = raw.match(/^(D|EE|EEE|E|M|W|EW|XW|2E|3E|4E|B|C)\s*(\d{1,2}(?:\.5)?)$/i) || raw.match(/^(\d{1,2}(?:\.5)?)\s*(D|EE|EEE|E|M|W|EW|XW|2E|3E|4E|B|C)$/i))) {
        const num = /^\d/.test(m[1]) ? m[1] : m[2], code = /^\d/.test(m[1]) ? m[2] : m[1];
        d.size = num; d.width = d.width || normWidth(code); // old combined boot sizes: "D 10", "10 EE"
      } else if (!(familyOf(raw) && sizeKey(normSize(raw))[0] === 2)) {
        d.size = normSize(raw); // skips colour words that ended up in a size option ("GREEN")
      }
    }
    return d;
  }
  function dimSort(key) {
    if (key === 'size') return sizeSort;
    if (key === 'width') return (a, b) => WIDTH_ORDER.indexOf(a) - WIDTH_ORDER.indexOf(b);
    if (key === 'waist' || key === 'length') return (a, b) => parseFloat(a) - parseFloat(b) || String(a).localeCompare(String(b));
    return (a, b) => String(a).localeCompare(String(b));
  }
  const MY_KEYS = { size: 'srMySize', waist: 'srMyWaist', length: 'srMyLength' }; // remembered per dimension

  /* ---------- helpers ---------- */
  // touch screens get this in place of the hover photo (they can't hover, and lazy images load even at opacity 0)
  const PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const resize = (url, w) => (url ? url.replace(/([?&])width=\d+/, `$1width=${w}`) : url);
  // colour squares on a card before "+N": 4 from tablet up, 3 on phones (keep in sync with snippets/sr-card.liquid)
  const SWATCH_MAX = 4;
  const SWATCH_MAX_PHONE = 3;
  const FACET_LABELS = { type: 'Type', brand: 'Brand', color: 'Color', size: 'Size', waist: 'Waist', length: 'Length', width: 'Width', lens: 'Lens', price: 'Price', stock: 'Availability' };
  const MULTI = ['type', 'brand', 'color', ...DIMS];
  // Donovan 2026-10-18: Type first whenever there's a choice, then Brand, then the size dimensions
  const DEFAULT_FACETS = ['type', 'brand', ...DIMS, 'price', 'color'];
  // infinite scroll: this many batches load on their own, then the Load more button takes over (Donovan 2026-10-19)
  const AUTO_BATCHES = 3;

  // Color cards (Donovan 2026-11-03): pages listed in the shop metafield custom.color_cards (kept by Claude from the
  // log Shopify Store/color-cards.md via Scripts/shopify-supply-collections/color-cards.mjs) show one card
  // per colour instead of one per product. One rule per line: "<handle|*> [key=value,value …]", "off" first to turn a
  // match off. Keys are the URL filter keys (type, brand, color, size…). Most specific match wins; no match = off.
  //   tees · brixton · tees brand=brixton · * type=beanie · off tees brand=dickies
  // Groups and wildcards (Donovan 2026-11-04, "brand + type"): `@brand` = any brand page (template collection.brand),
  // `@other` = any other collection; a value of `*` = any value picked. `@brand type=*` (a brand page with a type picked)
  // + `@other brand=*` (a type or department page with a brand picked) = colour cards whenever brand and type are both set.
  const norm = (x) => String(x).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  function parseColorRules(text) {
    return String(text || '').split(/\r?\n/).map((line) => line.replace(/#.*/, '').trim().split(/\s+/).filter(Boolean)).filter((t) => t.length).map((t) => {
      const off = t[0].toLowerCase() === 'off';
      const [handle, ...conds] = off ? t.slice(1) : t;
      if (!handle) return null;
      return {
        on: !off, handle: handle === '*' || handle[0] === '@' ? handle.toLowerCase() : norm(handle),
        conds: conds.map((c) => c.split('=')).filter(([k, v]) => k && v).map(([k, v]) => ({ k: k.toLowerCase(), any: v.trim() === '*', vals: v.split(',').map(norm).filter(Boolean) })),
      };
    }).filter(Boolean);
  }

  class SrCollection extends HTMLElement {
    $$(sel) { return [...this.querySelectorAll(sel), ...(this.drawer ? this.drawer.querySelectorAll(sel) : [])]; }
    $(sel) { return this.querySelector(sel) || (this.drawer && this.drawer.querySelector(sel)); }

    disconnectedCallback() {
      if (this.drawer && this.drawer.parentNode === document.body) this.drawer.remove();
      if (this.autoLoad) { this.autoLoad.disconnect(); this.autoLoad = null; }
    }

    connectedCallback() {
      this.handle = this.dataset.handle;
      this.perPage = Number(this.dataset.perPage) || 24;
      const listed = (this.dataset.facets || DEFAULT_FACETS.join(',')).split(',').map((s) => s.trim()).filter(Boolean);
      // older facet settings only list "size": the other size dimensions sit right after it
      const order = listed.flatMap((k) => (k === 'size' ? DIMS.filter((d) => d === 'size' || !listed.includes(d)) : [k]));
      // type always leads, brand second (when listed)
      this.facetOrder = [...['type', 'brand'].filter((k) => order.includes(k)), ...order.filter((k) => k !== 'type' && k !== 'brand')];
      this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.grid = this.querySelector('[data-grid]');
      // the filter drawer lives on <body> so nothing on the page (sticky header, filter bar) can stack above it
      this.drawer = this.drawer || this.querySelector('[data-drawer]');
      if (this.drawer.parentNode !== document.body) document.body.appendChild(this.drawer);
      this.colorRules = parseColorRules(this.dataset.colorCards);
      this.state = this.readState();
      if (!this.bound) { this.bound = true; this.bindStatic(); }
      this.bindSwatches(this.grid);
      this.syncSortSelect();
      this.load().catch((e) => this.fallback(e));
    }

    // the index could not be loaded: the server page stays, with real page links instead of dead controls
    fallback(e) {
      console.warn('[sr-collection] index load failed, keeping server page', e);
      this.classList.add('is-fallback');
      this.classList.remove('srx--cc-pending');
      const note = this.querySelector('[data-fallback-notice]');
      if (note) note.hidden = false;
      const label = this.querySelector('[data-count-label]');
      const n = label && Number(label.dataset.serverCount);
      if (label && n) label.textContent = `${n} ${n === 1 ? 'product' : 'products'}`;
    }

    /* ----- state ----- */
    readState() {
      const q = new URLSearchParams(location.search);
      const st = { type: [], brand: [], color: [], size: [], waist: [], length: [], width: [], lens: [], priceMin: null, priceMax: null, sort: 'featured', my: {} };
      const parsePrice = (v) => { const [a, b] = String(v).split('-'); st.priceMin = a !== '' && !Number.isNaN(+a) ? +a : null; st.priceMax = b !== undefined && b !== '' && !Number.isNaN(+b) ? +b : null; };
      let fromUrl = false;
      for (const k of MULTI) {
        if (q.has(k)) { st[k] = q.get(k).split(',').filter(Boolean); fromUrl = true; }
      }
      if (q.has('price')) { parsePrice(q.get('price')); fromUrl = true; }
      if (q.has('sort')) st.sort = q.get('sort');
      if (!fromUrl) {
        // per-collection presets (never brand)
        let presets = [];
        try { presets = JSON.parse(this.dataset.presets || 'null') || []; } catch (e) {}
        for (const p of presets) {
          const [k, ...rest] = String(p).split(':');
          const key = k.trim().toLowerCase(), val = rest.join(':').trim();
          if (key === 'brand' || !val) continue;
          if (key === 'price') parsePrice(val);
          else if (MULTI.includes(key)) st[key].push(val);
        }
      }
      // remembered size / waist / length (My size), applied once the index tells us which values exist here,
      // unless the link itself names that dimension. It is never written into the URL (a shared link must not carry it).
      this.pendingMy = {};
      for (const [k, storeKey] of Object.entries(MY_KEYS)) {
        if (q.has(k) || st[k].length) continue;
        try { this.pendingMy[k] = JSON.parse(localStorage.getItem(storeKey) || '[]'); } catch (e) {}
      }
      return st;
    }

    // URL sync is debounced: Safari throws after 100 replaceState calls in 30s (one slider drag could do that)
    writeUrl() {
      clearTimeout(this.urlTimer);
      this.urlTimer = setTimeout(() => this.writeUrlNow(), 300);
    }

    writeUrlNow() {
      const q = new URLSearchParams(location.search);
      for (const k of [...MULTI, 'price', 'stock', 'sort', 'page']) q.delete(k);
      for (const k of MULTI) if (this.state[k].length && !this.state.my[k]) q.set(k, this.state[k].join(','));
      if (this.priceActive()) q.set('price', `${this.state.priceMin ?? ''}-${this.state.priceMax ?? ''}`);
      if (this.state.sort !== 'featured') q.set('sort', this.state.sort);
      const s = q.toString();
      const next = location.pathname + (s ? `?${s.replace(/%2C/g, ',')}` : '');
      if (next === location.pathname + location.search) return;
      try { history.replaceState(history.state, '', next); } catch (e) { /* rate-limited: the next change retries */ }
    }

    /* ----- index ----- */
    async load() {
      const key = `srIdx3:${this.handle}`; // v3: + cp (colour sales positions, no units); v2: variant rows carry compare-at
      let data = null;
      try {
        const c = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (c && Date.now() - c.t < 10 * 60 * 1000 && c.count === Number(this.dataset.count)) data = c.products;
      } catch (e) {}
      if (!data) {
        // every page at once (the count is known server-side), each with a timeout and one retry; a bad status is an error
        const pages = Math.max(1, Math.ceil((Number(this.dataset.count) || 0) / 250));
        const got = await Promise.all(Array.from({ length: pages }, (_, i) => this.fetchPage(i + 1)));
        data = got.flatMap((r) => r.products);
        // the collection grew since the page rendered: fetch the pages the first response says exist
        const reported = (got[0] && got[0].pages) || pages;
        if (reported > pages) {
          const extra = await Promise.all(Array.from({ length: reported - pages }, (_, i) => this.fetchPage(pages + i + 1)));
          extra.forEach((r) => data.push(...r.products));
        }
        try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), count: Number(this.dataset.count), products: data })); } catch (e) {}
      }
      if (this.dataset.hideKids === 'true') data = data.filter((p) => !p.k); // kids only on kids collections
      data = data.filter((p) => p.img); // products with no photo never show (Donovan 2026-10-18)
      this.products = data.map((p, i) => this.prepare(p, i));
      this.buildFacetValues();
      for (const [k, vals] of Object.entries(this.pendingMy || {})) {
        const here = (vals || []).filter((x) => this.values[k].has(x));
        if (here.length && this.values[k].size > 1) { this.state[k] = here; this.state.my[k] = true; }
      }
      this.ready = true;
      // hero count = what this page can actually show (kids excluded)
      document.querySelectorAll('[data-sr-total]').forEach((el) => (el.textContent = `${this.products.length} ${this.products.length === 1 ? 'product' : 'products'}`));
      this.renderFacets();
      this.arrangeBrandStrip();
      this.apply({ keepServer: true });
      this.setupAutoLoad();
    }

    async fetchPage(p, attempt = 0) {
      const ctl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctl && setTimeout(() => ctl.abort(), 15000);
      try {
        const r = await fetch(`${this.dataset.url}?view=index&page=${p}`, { credentials: 'same-origin', signal: ctl ? ctl.signal : undefined });
        if (!r.ok) throw new Error(`index page ${p}: HTTP ${r.status}`);
        return await r.json();
      } catch (e) {
        if (attempt < 1) { await new Promise((res) => setTimeout(res, 700 + Math.random() * 600)); return this.fetchPage(p, attempt + 1); }
        throw e;
      } finally { if (timer) clearTimeout(timer); }
    }

    prepare(p, order) {
      const ci = p.ci, si = p.si;
      const colors = [];
      const colorMap = new Map();
      // lead for products without a colour option: the first in-stock variant, else the first
      let lead0 = null;
      for (const v of p.vs) {
        const [id, opts, avail, price, img, cmp] = v;
        if (!lead0 || (avail && !lead0.avail)) lead0 = { id, price, cmp: cmp || 0, avail: !!avail };
        const color = ci >= 0 ? opts[ci] : null;
        if (color !== null && !colorMap.has(color)) { colorMap.set(color, { name: color, img: null, id, price, cmp: cmp || 0, avail: false, fam: familyOf(color) }); colors.push(color); }
        const c = color !== null ? colorMap.get(color) : null;
        if (c) {
          if (!c.img && img) c.img = img;
          // price and compare-at follow the variant the card opens: the colour's first in-stock variant, else its first
          if (avail) { c.avail = true; if (!c.availId) { c.availId = id; c.price = price; c.cmp = cmp || 0; } }
        }
      }
      // ranking order first
      const rank = Array.isArray(p.cr) ? p.cr : [];
      const ordered = [...rank.filter((n) => colorMap.has(n)), ...colors.filter((n) => !rank.includes(n))];
      const vd = p.vs.map((v) => dimsOf(p, v[1]));
      // store-wide sales position per colour (ranking job, custom.color_pos_30d; "" = no colour option): orders colour cards
      const cp = p.cp && typeof p.cp === 'object' ? p.cp : {};
      colorMap.forEach((c) => { c.pos = cp[c.name] || Infinity; });
      if (lead0) lead0.pos = cp[''] || Math.min(Infinity, ...[...colorMap.values()].map((c) => c.pos));
      return {
        ...p, order, ci, si, vd, lead0, ty: displayType(p), rawType: p.ty,
        colors: ordered.map((n) => colorMap.get(n)),
        fams: new Set(ordered.map((n) => colorMap.get(n).fam).filter(Boolean)),
        url: `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}products/${p.h}`, // locale/market root
      };
    }

    buildFacetValues() {
      const v = { type: new Map(), brand: new Map(), color: new Map(), size: new Set(), waist: new Set(), length: new Set(), width: new Set(), lens: new Set() };
      let lo = Infinity, hi = 0;
      for (const p of this.products) {
        if (p.ty) v.type.set(p.ty, (v.type.get(p.ty) || 0) + 1);
        if (p.v) v.brand.set(p.v, (v.brand.get(p.v) || 0) + 1);
        p.fams.forEach((f) => v.color.set(f, (v.color.get(f) || 0) + 1));
        for (const d of p.vd) for (const k of DIMS) if (d[k] !== undefined && d[k] !== '') v[k].add(d[k]);
        if (p.p < lo) lo = p.p;
        if (p.p > hi) hi = p.p;
      }
      this.values = v;
      // price slider bounds in whole dollars, rounded out to $5
      this.priceBounds = lo === Infinity ? [0, 0] : [Math.floor(lo / 500) * 5, Math.ceil(hi / 500) * 5];
    }

    /* ----- filtering ----- */
    matches(p, skip) {
      const s = this.state;
      if (skip !== 'type' && s.type.length && !s.type.includes(p.ty)) return false;
      if (skip !== 'brand' && s.brand.length && !s.brand.includes(p.v)) return false;
      const pp = p.cc ? p.cc.price : p.p;
      if (skip !== 'price' && ((s.priceMin !== null && pp < s.priceMin * 100) || (s.priceMax !== null && pp > s.priceMax * 100))) return false;
      const wantColor = skip !== 'color' && s.color.length;
      const want = DIMS.filter((k) => skip !== k && s[k].length);
      const cc = p.cc; // colour card: only that colour's variants count
      if (!wantColor && !want.length) return cc ? cc.avail : p.a; // sold-out products (and colours) never show
      // one in-stock variant has to satisfy everything picked together (colour family + size/waist/length/width/lens)
      for (let i = 0; i < p.vs.length; i++) {
        const [, opts, avail] = p.vs[i];
        if (!avail) continue;
        if (cc && opts[p.ci] !== cc.name) continue;
        if (wantColor && !(p.ci >= 0 && s.color.includes(familyOf(opts[p.ci])))) continue;
        const d = p.vd[i];
        if (want.some((k) => !s[k].includes(d[k]))) continue;
        return true;
      }
      return false;
    }

    apply({ keepServer = false } = {}) {
      if (!this.ready) return;
      this.split = this.colorCardsOn();
      const cards = this.split ? this.colorCards() : this.products;
      let list = cards.filter((p) => this.matches(p));
      const sort = this.state.sort;
      const price = (p) => (p.cc ? p.cc.price : p.p);
      // colour cards: each colour by its own store-wide sales position, then the collection order; product cards: the
      // ranking job's order (best-selling first; the feed carries no sales figures)
      if (this.split && (sort === 'featured' || sort === 'best')) list.sort((a, b) => a.pos - b.pos || a.order - b.order || a.ci2 - b.ci2);
      else if (sort === 'best') list.sort((a, b) => a.order - b.order);
      else if (sort === 'new') list.sort((a, b) => b.n - a.n || a.order - b.order);
      else if (sort === 'price-asc') list.sort((a, b) => price(a) - price(b) || a.order - b.order);
      else if (sort === 'price-desc') list.sort((a, b) => price(b) - price(a) || a.order - b.order);
      this.list = list;
      this.page = 1;
      this.renderGrid(keepServer);
      if (!keepServer && this.drawer.hidden) this.scrollBack();
      if (this.autoLoad) { this.autoLeft = AUTO_BATCHES; this.querySelector('[data-more]').classList.add('is-auto'); requestAnimationFrame(() => this.autoLoadNext()); }
      this.renderChips();
      this.updateFacetCounts();
      this.writeUrl();
    }

    // is this page (handle + current filters) on the Color cards list?
    colorCardsOn() {
      let best = null, bestScore = -1;
      this.colorRules.forEach((rule, i) => {
        const brandPage = this.dataset.template === 'brand';
        const group = rule.handle === '*' || (rule.handle === '@brand' && brandPage) || (rule.handle === '@other' && !brandPage);
        if (!group && rule.handle !== norm(this.handle)) return;
        const ok = rule.conds.every(({ k, any, vals }) => Array.isArray(this.state[k]) && (any ? this.state[k].length > 0 : this.state[k].some((x) => vals.includes(norm(x)))));
        if (!ok) return;
        const score = rule.conds.length * 2 + (group ? 0 : 1);
        if (score >= bestScore) { best = rule; bestScore = score; } // ties: the later line wins
      });
      return !!(best && best.on);
    }

    // one entry per colour, titled "{Product} {Colour}" even when there's one colour (slowride.com); products with no colour
    // option stay one card. Built once, reused across filter changes.
    colorCards() {
      if (!this.cards) {
        this.cards = this.products.flatMap((p) => (p.colors.length
          ? p.colors.map((c, i) => Object.assign(Object.create(p), { cc: c, ci2: i, pos: c.pos, key: `${p.id}:${c.name}` }))
          : [Object.assign(Object.create(p), { ci2: 0, pos: p.lead0 ? p.lead0.pos : Infinity, key: String(p.id) })]));
      }
      return this.cards;
    }

    // a filter change rebuilds a shorter grid: bring its top back into view so the shopper isn't left in the footer
    scrollBack() {
      const top = this.querySelector('.srx__top') || this.grid;
      if (!top || top.getBoundingClientRect().top >= 0) return;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 0;
      window.scrollTo({ top: top.getBoundingClientRect().top + window.scrollY - header - 12 });
    }

    /* ----- rendering ----- */
    leadColor(p) {
      if (!p.colors.length) return null;
      const s = this.state;
      const fits = (c) => (!s.color.length || s.color.includes(c.fam));
      return p.colors.find((c) => c.avail && fits(c)) || p.colors.find(fits) || p.colors[0];
    }

    cardHTML(p, index) {
      const lead = p.cc || this.leadColor(p);
      const main = (lead && lead.img) || p.img;
      // colour cards skip the hover photo: the index has one photo per colour, and the product's 2nd photo is often another colour
      const alt = p.cc ? null : p.img2 && p.img2 !== main ? p.img2 : (p.img !== main ? p.img : null);
      const url = lead ? `${p.url}?variant=${lead.availId || lead.id}` : p.url;
      // price and Sale badge belong to the variant the card opens (never a sibling size's compare-at price)
      const priced = lead || p.lead0 || { price: p.p, cmp: 0 };
      const price = priced.price, cmp = priced.cmp || 0;
      const sale = cmp > price;
      const loading = index < 4 ? 'eager' : 'lazy';
      const img = (src, cls, altText) => `<img class="${cls}" src="${esc(resize(src, 720))}" srcset="${[360, 540, 720, 900, 1080].map((w) => `${esc(resize(src, w))} ${w}w`).join(', ')}" sizes="(max-width: 767px) 50vw, (max-width: 1099px) 33vw, 28vw" loading="${loading}" alt="${esc(altText)}">`;
      let sw = '';
      if (p.colors.length > 1) {
        sw = '<div class="srx-swatches">' + p.colors.slice(0, SWATCH_MAX).map((c, i) =>
          `<button type="button" class="srx-swatch${c === lead ? ' is-active' : ''}${i >= SWATCH_MAX_PHONE ? ' srx-swatch--wide' : ''}" data-url="${esc(p.url)}?variant=${c.availId || c.id}"${c.img ? ` data-img="${esc(resize(c.img, 720))}"` : ''} aria-label="${esc(c.name)}" title="${esc(c.name)}">${c.img ? `<img src="${esc(resize(c.img, 160))}" alt="" loading="lazy" width="76" height="76">` : ''}</button>`
        ).join('') + (p.colors.length > SWATCH_MAX_PHONE ? `<span class="srx-swatches__more srx-swatches__more--phone">+${p.colors.length - SWATCH_MAX_PHONE}</span>` : '') + (p.colors.length > SWATCH_MAX ? `<span class="srx-swatches__more srx-swatches__more--wide">+${p.colors.length - SWATCH_MAX}</span>` : '') + '</div>';
      }
      const soldOut = p.cc ? !p.cc.avail : !p.a;
      const title = p.cc ? `${p.t} ${p.cc.name}` : p.t;
      return `<article class="srx-card" data-id="${esc(p.key || p.id)}">
  <div class="srx-card__frame">
    <a class="srx-card__media" href="${esc(url)}" tabindex="-1" aria-hidden="true">${main ? img(main, 'srx-card__img', title) : ''}${alt ? `<picture class="srx-card__alt"><source media="(hover: none)" srcset="${PIXEL}">${img(alt, 'srx-card__img srx-card__img--alt', '').replace(`loading="${loading}"`, 'loading="lazy"')}</picture>` : ''}${soldOut ? '<span class="srx-badge srx-badge--muted">Sold out</span>' : sale ? '<span class="srx-badge">Sale</span>' : ''}</a>
    ${sw}
  </div>
  <div class="srx-card__body">
    <p class="srx-card__vendor">${esc(p.v)}</p>
    <h3 class="srx-card__title"><a href="${esc(url)}">${esc(title)}</a></h3>
    <p class="srx-card__price${sale ? ' is-sale' : ''}"><span>${this.money.format(price / 100)}</span>${sale ? ` <s>${this.money.format(cmp / 100)}</s>` : ''}</p>
  </div>
</article>`;
    }

    renderGrid(keepServer) {
      const slice = this.list.slice(0, this.page * this.perPage);
      const ids = slice.map((p) => String(p.key || p.id));
      const current = Array.from(this.grid.children).filter((el) => el.classList.contains('srx-card')).map((el) => el.dataset.id);
      // the server always paints product cards: colour cards (or a colour filter) repaint everything
      const samePrefix = keepServer && current.every((id, i) => ids[i] === id) && !this.state.color.length && !this.split;
      if (samePrefix) {
        // server page is still right: only append what's missing (no flash)
        const html = slice.slice(current.length).map((p, i) => this.cardHTML(p, current.length + i)).join('');
        if (html) this.grid.insertAdjacentHTML('beforeend', html);
      } else {
        this.grid.innerHTML = slice.map((p, i) => this.cardHTML(p, i)).join('');
      }
      this.bindSwatches(this.grid);
      this.classList.remove('srx--cc-pending'); // colour-card pages hide the server's product cards until this first paint
      const total = this.list.length;
      this.querySelector('[data-count-label]').textContent = `${total} ${total === 1 ? 'product' : 'products'}`;
      this.querySelector('[data-empty]').hidden = total > 0;
      const more = this.querySelector('[data-more]');
      more.hidden = slice.length >= total;
      this.querySelector('[data-progress]').textContent = `Showing ${slice.length} of ${total}`;
      const apply = this.$('[data-apply-label]');
      if (apply) apply.textContent = `Show ${total} ${total === 1 ? 'result' : 'results'}`;
    }

    // infinite scroll (Donovan 2026-10-18): the next batch loads before the shopper reaches the end of the grid.
    // One small batch at a time; card images below the fold are lazy, so this costs almost nothing until scrolled to.
    // The Load more button stays as the fallback (no IntersectionObserver).
    setupAutoLoad() {
      const more = this.querySelector('[data-more]');
      if (!more || !('IntersectionObserver' in window) || this.autoLoad) return;
      this.autoLeft = AUTO_BATCHES;
      more.classList.add('is-auto');
      this.autoLoad = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) this.autoLoadNext();
      }, { rootMargin: '0px 0px 1400px 0px' });
      this.autoLoad.observe(more);
    }

    autoLoadNext() {
      if (this.autoBusy || !this.list || this.page * this.perPage >= this.list.length) return;
      const more = this.querySelector('[data-more]');
      // after a few batches the button takes over, so the footer stays reachable and the page doesn't grow without end
      if (this.autoLeft <= 0) { more.classList.remove('is-auto'); return; }
      this.autoBusy = true;
      this.autoLeft -= 1;
      this.loadMore();
      // if the sentinel is still near the viewport (tall screen, short cards), keep going next frame
      requestAnimationFrame(() => {
        this.autoBusy = false;
        if (more && !more.hidden && more.getBoundingClientRect().top < innerHeight + 1400) this.autoLoadNext();
      });
    }

    loadMore(fromButton) {
      // a tap on Load more grants another run of automatic batches
      if (fromButton && this.autoLoad) { this.autoLeft = AUTO_BATCHES; this.querySelector('[data-more]').classList.add('is-auto'); }
      const start = this.page * this.perPage;
      this.page += 1;
      const html = this.list.slice(start, this.page * this.perPage).map((p, i) => this.cardHTML(p, start + i)).join('');
      this.grid.insertAdjacentHTML('beforeend', html);
      this.bindSwatches(this.grid);
      const shown = Math.min(this.page * this.perPage, this.list.length);
      this.querySelector('[data-more]').hidden = shown >= this.list.length;
      this.querySelector('[data-progress]').textContent = `Showing ${shown} of ${this.list.length}`;
    }

    bindSwatches(root) {
      root.querySelectorAll('.srx-swatch:not([data-bound])').forEach((b) => {
        b.dataset.bound = '1';
        const card = b.closest('.srx-card');
        const activate = () => {
          card.querySelectorAll('.srx-swatch.is-active').forEach((x) => x.classList.remove('is-active'));
          b.classList.add('is-active');
          const img = card.querySelector('.srx-card__img:not(.srx-card__img--alt)');
          if (img && b.dataset.img) {
          // keep a srcset so retina screens don't get a soft 720px file
          img.srcset = [360, 540, 720, 900, 1080].map((w) => `${resize(b.dataset.img, w)} ${w}w`).join(', ');
          img.src = resize(b.dataset.img, 720);
        }
          card.querySelectorAll('a').forEach((a) => (a.href = b.dataset.url));
        };
        b.addEventListener('mouseenter', activate);
        b.addEventListener('focus', activate);
        b.addEventListener('click', (e) => { e.preventDefault(); activate(); });
      });
    }

    // brand carousel (footwear, bottoms…): only brands this page can show, most products first
    arrangeBrandStrip() {
      const track = this.querySelector('[data-brand-track]');
      if (!track) return;
      const tiles = [...track.querySelectorAll('[data-brand-tile]')];
      tiles.forEach((t) => (t.hidden = !this.values.brand.has(t.dataset.brandTile)));
      tiles.sort((a, b) => (this.values.brand.get(b.dataset.brandTile) || 0) - (this.values.brand.get(a.dataset.brandTile) || 0)).forEach((t) => track.appendChild(t));
      const strip = this.querySelector('[data-brand-strip]');
      strip.hidden = tiles.filter((t) => !t.hidden).length < 2;
      // start at the far left once after reordering (scroll-snap could leave it mid-row), and never again:
      // the logos lazy-load as the strip is swiped, and resetting on each load snapped the strip back to the start
      track.scrollLeft = 0;
      requestAnimationFrame(() => { track.scrollLeft = 0; });
    }

    /* ----- facets UI ----- */
    facetOptions(key) {
      const v = this.values;
      if (key === 'type') return [...v.type.keys()].sort().map((x) => ({ value: x, label: x }));
      if (key === 'brand') return [...v.brand.keys()].sort((a, b) => a.localeCompare(b)).map((x) => ({ value: x, label: x }));
      if (key === 'color') return FAMILIES.filter(([k]) => v.color.has(k)).map(([k, label, hex]) => ({ value: k, label, hex }));
      if (DIMS.includes(key)) return [...v[key]].sort(dimSort(key)).map((x) => ({ value: x, label: x }));
      return [];
    }

    visibleFacets() {
      return this.facetOrder.filter((k) => {
        if (k === 'stock') return false; // removed (Donovan): sold-out products are always hidden
        if (k === 'price') return this.priceBounds[1] > this.priceBounds[0];
        if (k === 'type') return this.values.type.size > 1;
        if (DIMS.includes(k)) return this.values[k].size > 1;
        return this.facetOptions(k).length > 1;
      });
    }

    facetBody(key) {
      if (key === 'price') {
        const [lo, hi] = this.priceBounds;
        const a = this.state.priceMin ?? lo, b = this.state.priceMax ?? hi;
        return `<div class="srx-price" data-price>
  <div class="srx-price__vals"><span data-price-min-label>$${a}</span><span data-price-max-label>$${b}</span></div>
  <div class="srx-price__track"><span class="srx-price__fill" data-price-fill></span>
    <input type="range" min="${lo}" max="${hi}" step="1" value="${a}" data-price-min aria-label="Minimum price">
    <input type="range" min="${lo}" max="${hi}" step="1" value="${b}" data-price-max aria-label="Maximum price">
  </div>
</div>`;
      }
      const opts = this.facetOptions(key);
      const cls = ['size', 'waist', 'length', 'width'].includes(key) ? 'srx-opts srx-opts--chips' : key === 'color' ? 'srx-opts srx-opts--colors' : 'srx-opts';
      return `<div class="${cls}">` + opts.map((o) => {
        const on = this.state[key].includes(o.value);
        return `<label class="srx-opt" data-facet="${key}" data-value="${esc(o.value)}"><input type="checkbox" ${on ? 'checked' : ''}>${o.hex ? `<span class="srx-dot" style="background:${o.hex}"></span>` : ''}<span class="srx-opt__label">${esc(o.label)}</span><span class="srx-opt__count" data-count-for="${key}:${esc(o.value)}"></span></label>`;
      }).join('') + '</div>';
    }

    groupsHTML(openFirst) {
      let n = 0;
      return this.visibleFacets().map((k) => {
        const open = n++ < openFirst; // Donovan: every group starts collapsed (counts show what's active)
        return `<details class="srx-group" ${open ? 'open' : ''}><summary>${FACET_LABELS[k]}<span class="srx-pill__n" data-pill-n="${k}"></span></summary>${this.facetBody(k)}</details>`;
      }).join('');
    }

    sortHTML() {
      const opts = [['featured', 'Featured'], ['best', 'Best selling'], ['new', 'Newest'], ['price-asc', 'Price: low to high'], ['price-desc', 'Price: high to low']];
      return `<details class="srx-group srx-group--sort"><summary>Sort<span class="srx-group__value" data-sort-value></span></summary><div class="srx-opts">${opts.map(([v, l]) =>
        `<label class="srx-opt srx-opt--radio"><input type="radio" name="srx-sort" value="${v}" data-sort-radio ${this.state.sort === v ? 'checked' : ''}><span class="srx-opt__label">${l}</span></label>`).join('')}</div></details>`;
    }

    renderFacets() {
      // desktop sidebar: every filter, all groups collapsed
      this.$('[data-side-body]').innerHTML = this.groupsHTML(0);
      // phone drawer: sort first, then the filters
      this.$('[data-drawer-body]').innerHTML = this.groupsHTML(0); // sort lives in the top row on every screen (Donovan, 2026-10-18)
      this.syncSortSelect();
    }

    updateFacetCounts() {
      // counts reflect every other active filter (classic faceting)
      for (const key of MULTI) {
        const counts = new Map();
        for (const p of this.products) {
          if (!this.matches(p, key)) continue;
          if (key === 'type') counts.set(p.ty, (counts.get(p.ty) || 0) + 1);
          else if (key === 'brand') counts.set(p.v, (counts.get(p.v) || 0) + 1);
          else if (key === 'color') p.fams.forEach((f) => counts.set(f, (counts.get(f) || 0) + 1));
          else if (DIMS.includes(key)) {
            const seen = new Set();
            p.vs.forEach((v, i) => { const x = p.vd[i][key]; if (v[2] && x !== undefined && !seen.has(x)) { seen.add(x); counts.set(x, (counts.get(x) || 0) + 1); } });
          }
        }
        this.$$(`[data-count-for^="${key}:"]`).forEach((el) => {
          const n = counts.get(el.dataset.countFor.slice(key.length + 1)) || 0;
          el.textContent = n;
          el.closest('.srx-opt').classList.toggle('is-empty', n === 0 && !this.state[key].includes(el.dataset.countFor.slice(key.length + 1)));
        });
        this.$$(`[data-pill-n="${key}"]`).forEach((el) => (el.textContent = this.state[key].length ? this.state[key].length : ''));
      }
      this.syncPrice();
      this.$$('[data-pill-n="price"]').forEach((el) => (el.textContent = this.priceActive() ? 1 : ''));
      this.$$('.srx-opt[data-facet]').forEach((el) => {
        el.querySelector('input').checked = this.state[el.dataset.facet].includes(el.dataset.value);
      });
      const n = MULTI.reduce((a, k) => a + this.state[k].length, 0) + (this.priceActive() ? 1 : 0);
      const badge = this.querySelector('[data-active-count]');
      badge.hidden = !n; badge.textContent = n;
      const clearSide = this.querySelector('[data-clear-side]');
      if (clearSide) clearSide.hidden = !n;
      this.querySelectorAll('[data-brand-tile]').forEach((b) => b.setAttribute('aria-pressed', this.state.brand.includes(b.dataset.brandTile)));
    }

    renderChips() {
      const chips = [];
      const label = (k, v) => {
        if (k === 'color') return (FAMILIES.find((f) => f[0] === v) || [])[1] || v;
        if (this.state.my[k]) return `My ${k}: ${v}`;
        if (k === 'waist') return `Waist ${v}`;
        if (k === 'length') return `Length ${v}`;
        return v;
      };
      for (const k of MULTI) for (const v of this.state[k]) chips.push(`<button type="button" class="srx-chip" data-chip="${k}" data-value="${esc(v)}">${esc(label(k, v))}<span aria-hidden="true">×</span><span class="sr-only">Remove</span></button>`);
      if (this.priceActive()) {
        const [lo, hi] = this.priceBounds;
        chips.push(`<button type="button" class="srx-chip" data-chip="price" data-value="">$${this.state.priceMin ?? lo} – $${this.state.priceMax ?? hi}<span aria-hidden="true">×</span><span class="sr-only">Remove</span></button>`);
      }
      if (chips.length) chips.push('<button type="button" class="srx-link" data-clear-all>Clear all</button>');
      this.querySelector('[data-chips]').innerHTML = chips.join('');
    }

    /* ----- events ----- */
    priceActive() { return this.state.priceMin !== null || this.state.priceMax !== null; }

    syncPrice() {
      const [lo, hi] = this.priceBounds || [0, 0];
      const a = this.state.priceMin ?? lo, b = this.state.priceMax ?? hi;
      const pct = (x) => (hi > lo ? ((x - lo) / (hi - lo)) * 100 : 0);
      this.$$('[data-price]').forEach((el) => {
        const mn = el.querySelector('[data-price-min]'), mx = el.querySelector('[data-price-max]');
        if (document.activeElement !== mn) mn.value = a;
        if (document.activeElement !== mx) mx.value = b;
        el.querySelector('[data-price-min-label]').textContent = `$${a}`;
        el.querySelector('[data-price-max-label]').textContent = `$${b}`;
        el.style.setProperty('--a', `${pct(a)}%`);
        el.style.setProperty('--b', `${pct(b)}%`);
      });
    }

    onPriceInput(input) {
      const wrap = input.closest('[data-price]');
      const [lo, hi] = this.priceBounds;
      let a = +wrap.querySelector('[data-price-min]').value, b = +wrap.querySelector('[data-price-max]').value;
      if (a > b) { if (input.matches('[data-price-min]')) a = b; else b = a; input.value = input.matches('[data-price-min]') ? a : b; }
      this.state.priceMin = a > lo ? a : null;
      this.state.priceMax = b < hi ? b : null;
      this.syncPrice();
      // while dragging, re-filter at most every 150ms (a full apply on every frame stuttered on big collections)
      if (!this.priceTimer) this.priceTimer = setTimeout(() => { this.priceTimer = null; this.apply(); }, 150);
    }

    toggle(key, value) {
      if (key === 'price') { this.state.priceMin = null; this.state.priceMax = null; this.apply(); return; }
      const arr = this.state[key];
      const i = arr.indexOf(value);
      if (i >= 0) arr.splice(i, 1); else arr.push(value);
      if (MY_KEYS[key]) {
        this.state.my[key] = arr.length > 0;
        try { arr.length ? localStorage.setItem(MY_KEYS[key], JSON.stringify(arr)) : localStorage.removeItem(MY_KEYS[key]); } catch (e) {}
      }
      this.apply();
    }

    clearAll() {
      for (const k of MULTI) this.state[k] = [];
      this.state.priceMin = null;
      this.state.priceMax = null;
      this.state.my = {};
      try { Object.values(MY_KEYS).forEach((k) => localStorage.removeItem(k)); } catch (e) {}
      this.apply();
    }

    syncSortSelect() {
      const s = this.querySelector('[data-sort]');
      if (s) s.value = this.state.sort;
      this.$$('[data-sort-radio]').forEach((r) => (r.checked = r.value === this.state.sort));
      const label = { featured: 'Featured', best: 'Best selling', new: 'Newest', 'price-asc': 'Price ↑', 'price-desc': 'Price ↓' }[this.state.sort];
      this.$$('[data-sort-value]').forEach((el) => (el.textContent = label || ''));
    }

    bindStatic() {
      [this, this.drawer].forEach((root) => this.bindRoot(root));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeDrawer(); else this.trapTab(e); });
    }

    bindRoot(root) {
      root.addEventListener('input', (e) => { if (e.target.matches('[data-price-min], [data-price-max]')) this.onPriceInput(e.target); });
      root.addEventListener('change', (e) => {
        if (e.target.matches('[data-price-min], [data-price-max]')) { clearTimeout(this.priceTimer); this.priceTimer = null; this.apply(); return; }
        const t = e.target;
        if (t.matches('[data-sort], [data-sort-radio]')) { this.state.sort = t.value; this.syncSortSelect(); this.apply(); return; }
        const opt = t.closest('.srx-opt');
        if (opt) this.toggle(opt.dataset.facet, opt.dataset.value);
      });
      root.addEventListener('click', (e) => {
        const t = e.target;
        const chip = t.closest('[data-chip]');
        if (chip) { this.toggle(chip.dataset.chip, chip.dataset.value); return; }
        const tile = t.closest('[data-brand-tile]');
        if (tile) { this.toggle('brand', tile.dataset.brandTile); return; }
        if (t.closest('[data-clear-all]')) { this.clearAll(); return; }
        if (t.closest('[data-load-more]')) { this.loadMore(true); return; }
        if (t.closest('[data-open-drawer]')) { this.openDrawer(); return; }
        if (t.closest('[data-close-drawer]')) { this.closeDrawer(); return; }
      });
    }


    openDrawer() {
      const d = this.drawer;
      clearTimeout(this.hideTimer);
      this.lastFocus = document.activeElement;
      d.hidden = false;
      requestAnimationFrame(() => d.classList.add('is-open'));
      document.documentElement.classList.add('srx-lock');
      const first = d.querySelector('.srx__drawer-head [data-close-drawer]');
      if (first) first.focus({ preventScroll: true });
    }

    closeDrawer() {
      const d = this.drawer;
      if (d.hidden) return;
      d.classList.remove('is-open');
      document.documentElement.classList.remove('srx-lock');
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => { d.hidden = true; }, 300);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      this.scrollBack();
    }

    // keep Tab inside the open filter sheet (it is a modal dialog)
    trapTab(e) {
      const d = this.drawer;
      if (e.key !== 'Tab' || d.hidden) return;
      const items = [...d.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  customElements.define('sr-collection', SrCollection);
})();
